import React, { useState } from "react";
import { DotLegalMultiSelect } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation.ts";
import LegalEntityTypeUsageDialog from "../../../legalEntity/legalEntityTypeUsageDialog/LegalEntityTypeUsageDialog.tsx";
import { getLegalEntityTypeSelectOptions } from "./legalEntityFieldOptions.ts";
import { useLegalEntityTypeNames } from "../../../legalEntity/useLegalEntityTypeNames.ts";
import { StandardLegalEntityType } from "../../../legalEntity/LegalEntity.types.ts";

export interface LegalEntityTypesFieldProps {
    legalEntityId: string;
    types: Array<string>;
    onChange: (types: Array<string>) => void;
    // Being used as a processor on a transfer is what grants these types, so they cannot be taken off here.
    // Clicking such a chip explains where the type comes from instead.
    isDataProcessorOnTransfers?: boolean;
    isSubDataProcessorOnTransfers?: boolean;
    readOnly?: boolean;
    disabled: boolean;
    errorText?: string;
}

function LegalEntityTypesField(props: LegalEntityTypesFieldProps) {
    const { translateString } = useTranslation();
    const [typeExplained, setTypeExplained] = useState<string | null>(null);
    const { legalEntityTypes, getStandardTypeId, isLoadingLegalEntityTypes } = useLegalEntityTypeNames();

    const unDeletableTypes = getUnDeletableTypes();

    return (
        <>
            <DotLegalMultiSelect
                options={getLegalEntityTypeSelectOptions(legalEntityTypes, unDeletableTypes)}
                chosenOptions={props.types}
                onChange={props.readOnly ? () => {} : (types) => props.onChange(types)}
                onChipClick={(type) => explainUnDeletableType(type)}
                placeholder={translateString("type")}
                label={translateString("type")}
                noOptionsLabel={translateString("noOptions")}
                isLoading={isLoadingLegalEntityTypes}
                disabled={props.disabled}
                errorText={props.errorText}
            />

            {typeExplained !== null && (
                <LegalEntityTypeUsageDialog
                    legalEntityId={props.legalEntityId}
                    legalEntityTypeId={typeExplained}
                    onDialogClose={() => setTypeExplained(null)}
                />
            )}
        </>
    );

    function explainUnDeletableType(typeId: string) {
        if (unDeletableTypes.includes(typeId)) {
            setTypeExplained(typeId);
        }
    }

    function getUnDeletableTypes(): Array<string> {
        const unDeletableTypes: Array<string> = [];

        addWhenUsedAsProcessor(unDeletableTypes, props.isDataProcessorOnTransfers, StandardLegalEntityType.DataProcessor);
        addWhenUsedAsProcessor(unDeletableTypes, props.isSubDataProcessorOnTransfers, StandardLegalEntityType.SubDataProcessor);

        return unDeletableTypes;
    }

    function addWhenUsedAsProcessor(unDeletableTypes: Array<string>, isUsedAsProcessor: boolean | undefined, standardType: StandardLegalEntityType) {
        const typeId = getStandardTypeId(standardType);
        if (isUsedAsProcessor && typeId !== undefined) {
            unDeletableTypes.push(typeId);
        }
    }
}

export default LegalEntityTypesField;
